// Angular
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';

// Environment
import { environment } from 'environments/environment';

// RxJS
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import * as auth from 'app/auth/service/index';
import * as HttpService from 'app/http/service/index';
import { CoreConfigService } from '@core/services/config.service';

// Validators
import { ValidObjectID } from 'app/validators/object-id.service';

// External Modules
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-auth-request-access-file',
  templateUrl: './auth-request-access-file.component.html',
  styleUrls: ['./auth-request-access-file.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AuthRequestAccessFileComponent implements OnInit, OnDestroy {
  //  Public
  public coreConfig: any;
  public registerForm: UntypedFormGroup = this._formBuilder.group({
    id: ['', ValidObjectID(true)],
    password: ['', [Validators.required, Validators.minLength(22), Validators.maxLength(22)]],
    privacyPolicyTerms: [false, Validators.requiredTrue],
  });
  public submitted = false;
  public passwordTextType: boolean;
  public privacyPolicyTerms = environment.privacyPolicyTerms === '' ? 'javascript:void(0)' : environment.privacyPolicyTerms;
  public returnUrl: string;

  // Private
  private _unsubscribeAll: Subject<any>;

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _formBuilder: UntypedFormBuilder,
    private _coreConfigService: CoreConfigService,
    private _httpResponseService: HttpService.ResponseService,
    private _authenticationService: auth.AuthenticationService,
  ) {
    this._unsubscribeAll = new Subject();

    // Configure the layout
    this._coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        menu: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        customizer: false,
        enableLocalStorage: false,
      },
    };
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.registerForm.controls;
  }

  /**
   * Toggle password
   */
  togglePasswordTextType() {
    this.passwordTextType = !this.passwordTextType;
  }

  /**
   * On Submit
   */
  onSubmit() {
    this.submitted = true;

    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }

    this._authenticationService
      .downloadAccessFile(this.registerForm.get('id').value, this.registerForm.get('password').value)
      .subscribe({
        next: (file) => {
          saveAs(this.objToBlob(file), 'access-file.json');
          this._router.navigate(['/']);
        },
        error: (err) => this._httpResponseService.manageErrors(err),
      });
  }

  objToBlob(data: Object): Blob {
    return new Blob([new TextEncoder().encode(JSON.stringify(data))], {
      type: 'application/json;charset=utf-8',
    });
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Get return url from route parameters or default to '/dashboard'
    this.returnUrl = this._route.snapshot.queryParams['returnUrl'] || '/authentication/login';

    // Get id and temporal password from route parameters
    if (this._route.snapshot.queryParams['idUser'] !== undefined) {
      this.registerForm.get('id').setValue(this._route.snapshot.queryParams['idUser']);
      this.registerForm.updateValueAndValidity();
    }
    if (!this._route.snapshot.queryParams['password'] !== undefined) {
      this.registerForm.get('password').setValue(this._route.snapshot.queryParams['password']);
      this.registerForm.updateValueAndValidity();
    }

    // Subscribe to config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
      this.coreConfig = config;
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
